import React, { useState, useEffect } from 'react';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import FormSelect from '../../common/FormSelect';
import Pagination from '../../common/Pagination';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import { addCsrfTokenToConfig } from '../../../utils/csrfUtils';
import './styles.css';

/**
 * 系統日誌組件
 * 用於顯示系統操作日誌
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @returns {JSX.Element} 系統日誌組件
 */
function Logs({ token }) {
    // 日誌數據狀態
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 過濾和搜索狀態
    const [searchTerm, setSearchTerm] = useState('');
    const [actionFilter, setActionFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    // 排序和分頁狀態
    const [sortField, setSortField] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [logsPerPage, setLogsPerPage] = useState(50);

    // 獲取日誌數據
    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError(null);

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            };

            // 添加CSRF令牌
            const configWithCsrf = addCsrfTokenToConfig(config);

            const response = await fetch('/api/auth/login/logs', {
                headers: configWithCsrf.headers
            });

            if (!response.ok) {
                throw new Error(`獲取日誌失敗: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (Array.isArray(data)) {
                setLogs(data);
            } else {
                throw new Error('日誌數據格式錯誤');
            }
        } catch (err) {
            setError(err.message || '獲取日誌失敗');
            console.error('獲取日誌錯誤:', err);
        } finally {
            setLoading(false);
        }
    };

    // 初始加載
    useEffect(() => {
        if (token) {
            fetchLogs();
        }
    }, [token]);

    // 當每頁記錄數量改變時，重置到第一頁
    useEffect(() => {
        setCurrentPage(1);
    }, [logsPerPage]);

    // 處理搜索
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    // 處理動作過濾
    const handleActionFilter = (e) => {
        setActionFilter(e.target.value);
        setCurrentPage(1);
    };

    // 處理用戶過濾
    const handleUserFilter = (e) => {
        setUserFilter(e.target.value);
        setCurrentPage(1);
    };

    // 處理日期過濾
    const handleDateFilter = (e) => {
        setDateFilter(e.target.value);
        setCurrentPage(1);
    };

    // 處理排序
    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // 過濾日誌
    const filteredLogs = logs.filter(log => {
        // 搜索詞過濾
        const searchMatch =
            !searchTerm ||
            (log.username && log.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.action && log.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.details && log.details.toLowerCase().includes(searchTerm.toLowerCase()));

        // 動作過濾
        const actionMatch = !actionFilter || (log.action === actionFilter);

        // 用戶過濾
        const userMatch = !userFilter || (log.username === userFilter);

        // 日期過濾
        const dateMatch = !dateFilter || (log.timestamp && log.timestamp.startsWith(dateFilter));

        return searchMatch && actionMatch && userMatch && dateMatch;
    });

    // 排序日誌
    const sortedLogs = [...filteredLogs].sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';

        if (sortField === 'timestamp') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }

        if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    // 計算分頁數據
    const indexOfLastLog = currentPage * logsPerPage;
    const indexOfFirstLog = indexOfLastLog - logsPerPage;
    const currentLogs = sortedLogs.slice(indexOfFirstLog, indexOfLastLog);
    const totalPages = Math.ceil(sortedLogs.length / logsPerPage);

    // 處理頁碼變更
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // 處理每頁記錄數量變更
    const handleLogsPerPageChange = (e) => {
        setLogsPerPage(Number(e.target.value));
    };

    // 獲取唯一的動作類型
    const uniqueActions = [...new Set(logs.map(log => log.action).filter(Boolean))];

    // 獲取唯一的用戶名
    const uniqueUsers = [...new Set(logs.map(log => log.username).filter(Boolean))];

    // 格式化時間戳
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // 獲取排序圖標
    const getSortIcon = (field) => {
        if (field !== sortField) return null;
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    };

    // 每頁記錄數量選項
    const logsPerPageOptions = [
        { value: 20, label: '20 條' },
        { value: 50, label: '50 條' },
        { value: 100, label: '100 條' }
    ];

    // 動作過濾選項
    const actionOptions = [
        { value: '', label: '全部動作' },
        ...uniqueActions.map(action => ({ value: action, label: action }))
    ];

    // 用戶過濾選項
    const userOptions = [
        { value: '', label: '全部用戶' },
        ...uniqueUsers.map(user => ({ value: user, label: user }))
    ];

    // 加載中狀態
    if (loading) {
        return <Loading text="正在加載日誌數據..." />;
    }

    return (
        <div className="logs-container">
            {/* 頁面標題 */}
            <div className="logs-header">
                <h2 className="logs-title">系統日誌</h2>
            </div>

            {/* 錯誤信息 */}
            {error && (
                <ErrorMessage
                    message={error}
                    onRetry={fetchLogs}
                />
            )}

            {/* 過濾和搜索卡片 */}
            <Card className="logs-filter-card">
                <div className="logs-filter-container">
                    <div className="logs-search">
                        <FormInput
                            id="searchTerm"
                            placeholder="搜索日誌..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="logs-search-input"
                        />
                    </div>

                    <div className="logs-filters">
                        <FormSelect
                            id="actionFilter"
                            label="動作"
                            value={actionFilter}
                            onChange={handleActionFilter}
                            options={actionOptions}
                            className="logs-filter-select"
                        />

                        <FormSelect
                            id="userFilter"
                            label="用戶"
                            value={userFilter}
                            onChange={handleUserFilter}
                            options={userOptions}
                            className="logs-filter-select"
                        />

                        <FormInput
                            id="dateFilter"
                            label="日期"
                            type="date"
                            value={dateFilter}
                            onChange={handleDateFilter}
                            className="logs-filter-date"
                        />
                    </div>

                    <div className="logs-controls">
                        <FormSelect
                            id="logsPerPage"
                            label="每頁顯示"
                            value={logsPerPage}
                            onChange={handleLogsPerPageChange}
                            options={logsPerPageOptions}
                            className="logs-per-page-select"
                        />

                        <Button
                            variant="secondary"
                            onClick={fetchLogs}
                            className="refresh-button"
                        >
                            刷新
                        </Button>
                    </div>
                </div>
            </Card>

            {/* 日誌表格卡片 */}
            <Card className="logs-table-card">
                <div className="logs-table-container">
                    <table className="logs-table">
                        <thead>
                            <tr>
                                <th
                                    className="timestamp-column"
                                    onClick={() => handleSort('timestamp')}
                                >
                                    時間{getSortIcon('timestamp')}
                                </th>
                                <th
                                    className="user-column"
                                    onClick={() => handleSort('username')}
                                >
                                    用戶{getSortIcon('username')}
                                </th>
                                <th
                                    className="action-column"
                                    onClick={() => handleSort('action')}
                                >
                                    動作{getSortIcon('action')}
                                </th>
                                <th className="details-column">詳情</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="logs-empty">暫無日誌數據</td>
                                </tr>
                            ) : (
                                currentLogs.map((log) => (
                                    <tr key={log.id || `log-${Math.random()}`}>
                                        <td className="timestamp-column">{formatTimestamp(log.timestamp)}</td>
                                        <td className="user-column">{log.username || 'N/A'}</td>
                                        <td className="action-column">{log.action || 'N/A'}</td>
                                        <td className="details-column">{log.details || '無詳細信息'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* 分頁 */}
            {sortedLogs.length > 0 && (
                <div className="logs-pagination">
                    <div className="logs-page-info">
                        顯示 {indexOfFirstLog + 1}-{Math.min(indexOfLastLog, sortedLogs.length)} 條，共 {sortedLogs.length} 條
                    </div>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
}

export default Logs; 